import { useQueryClient } from "@tanstack/react-query";

import ProjectsService from "@/services/ProjectsService";

import { useModal } from "@/hooks/useModal";

import { TProject } from "@/types";

import { promiseToast } from "@/modules/index";

/**
 * The project mutations the context menu and the project page share:
 * archive and delete. Each toasts and invalidates the project's own
 * queries plus the views', which gather projects - and the subscription,
 * since an archived project leaves the plan's project count.
 *
 * A project is not moved anywhere any more: it belongs to its client,
 * and a view points at it (docs/modele.md, phase 6). Syncing the app
 * state's active project stays with the caller; archiving confirms here,
 * deleting with the caller.
 */
export const useProjectActions = () => {
	const queryClient = useQueryClient();
	const { openModal } = useModal();

	const invalidate = () =>
		queryClient.invalidateQueries({
			predicate: (query) =>
				query.queryKey[0] === "views" ||
				query.queryKey[0] === "analytics" ||
				query.queryKey[0] === "projects" ||
				query.queryKey[0] === "tasks" ||
				query.queryKey[0] === "subscription",
		});

	const toggleArchived = (project: Pick<TProject, "id" | "isArchived">) => {
		const prom = ProjectsService.setProjectArchived(
			project.id,
			!project.isArchived
		).then(invalidate);
		promiseToast(prom);
		return prom;
	};

	/**
	 * Archiving asks first (ArchiveConfirmModal): the promise settles once
	 * confirmed and done, and never on a cancel - so what a caller chains
	 * on it only runs when the project was archived. Unarchiving asks
	 * nothing; it takes a slot of the plan back, a 402 opens the upgrade
	 * modal.
	 */
	const archive = (
		project: Pick<TProject, "id" | "isArchived" | "name">
	): Promise<void> =>
		project.isArchived
			? toggleArchived(project)
			: new Promise<void>((resolve, reject) =>
					openModal({
						type: "archiveConfirm",
						props: {
							kind: "project",
							name: project.name,
							onConfirm: () =>
								void toggleArchived(project).then(
									resolve,
									reject
								),
						},
					})
				);

	const remove = (project: Pick<TProject, "id">) => {
		const prom = ProjectsService.deleteProject(project.id).then(invalidate);
		promiseToast(prom);
		return prom;
	};

	return { archive, remove };
};
